import { ReactElement, cloneElement } from 'react';
import {
  Table,
  Input,
  Popconfirm,
  TableProps,
  Space,
  Dropdown,
  Skeleton,
  Empty,
  MenuProps,
  InputNumber,
  Grid,
} from 'antd';
import { IPageInfoResponse } from 'interfaces/general';
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  EyeOutlined,
  MoreOutlined,
  FilterFilled,
} from '@ant-design/icons';
import { defDelPopConf, sortDirections } from './constants';
import { Button } from 'app/atoms';
import { useTranslation } from 'react-i18next';
import { ColumnType as AntdColumnType } from 'antd/lib/table';
import StatusFilter from 'app/atoms/StatusFilter';
import { createNumberFormatter } from 'utils/date-time';

const { Search } = Input;

export type IRemoveClickEvent = React.MouseEvent<HTMLElement, MouseEvent> | undefined;
export type IEditClickEvent = React.MouseEvent<HTMLSpanElement, MouseEvent>;
export type IViewClickEvent = React.MouseEvent<HTMLSpanElement, MouseEvent>;

export interface ColumnType<T> extends AntdColumnType<T> {
  showStatusFilter?: boolean;
  statusFilters?: { text: string; value: string }[];
  searchable?: boolean;
  showNumberFilter?: boolean;
  filterPlaceholder?: string;
}

export interface ITableAction {
  key: string;
  label?: (record: any) => void;
  onClick?: (record: any) => void;
  icon?: ReactElement;
  disabled?: (record: any) => void;
}

interface ITableActionWithSubmenu extends ITableAction {
  subMenu?: ITableActionWithSubmenu[];
}

interface IEnhancedTableProps extends TableProps<any> {
  columns: ColumnType<any>[];
  onSearch: (_value: string, _dataIndex: string) => void;
  onEdit: (_event: IEditClickEvent, _record: any) => void;
  onView: (_event: IViewClickEvent, _record: any) => void;
  onRemove: (_event: IRemoveClickEvent, _record: any) => void;
  pageInfo?: IPageInfoResponse;
  delPopConfirmConf?: {
    title: string;
    desc: string;
  };
  showEdit: boolean;
  showRemove: boolean;
  showView: boolean;
  showMenu?: boolean;
  showSearch?: boolean;
  showSort?: boolean;
  showAssignCustomer?: boolean;
  showSelection?: boolean;
  selectedRowKeys?: React.Key[];
  onSelectionChange?: (selectedRowKeys: React.Key[], selectedRows: any[]) => void;
  actions?: ITableAction[];
  filteredColumnsInfo?: any;
  setFilteredColumnsInfo: Function;
  sortAndFilterInfo: Record<string, string | null>;
  loading: boolean;
  showAddTripToInvoiceBtn?: boolean;
  onAddTripToInvoice?: (tripId: number, tripName: string) => void;
  selectedTrips?: { value: number; label: string }[];
  showViewTripDetailsBtn?: boolean;
  onViewTripDetails?: (tripId: number, customerId: number) => void;
  breakpoint?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl';
}

const EnhancedTable = (props: IEnhancedTableProps): ReactElement => {
  const {
    columns = [],
    onSearch,
    onEdit,
    onView,
    onRemove,
    delPopConfirmConf = defDelPopConf,
    showEdit,
    showView,
    showRemove,
    showMenu,
    showSelection,
    onSelectionChange,
    // showSearch = false,
    pageInfo,
    dataSource,
    actions = [],
    setFilteredColumnsInfo,
    filteredColumnsInfo,
    loading,
    showAddTripToInvoiceBtn = false,
    onAddTripToInvoice,
    selectedTrips = [],
    showViewTripDetailsBtn = false,
    onViewTripDetails,
    selectedRowKeys,
    breakpoint = 'md',
    ...otherProps
  } = props;

  const { t } = useTranslation();

  // Use Ant Design's built-in breakpoint hook for efficient responsive behavior
  const screens = Grid.useBreakpoint();
  // Check if screen is below the specified breakpoint
  const isMobileOrTablet = !screens[breakpoint];

  const formatNumber = (num: number) => {
    const formatter = createNumberFormatter();
    return formatter.format(num);
  };

  const getFilterDropdownRenderer = (column: ColumnType<any>) =>
    function renderFilterDropdown(filterProps: any) {
      if (column.showStatusFilter && column.statusFilters) {
        // Get the current filter value from filteredColumnsInfo instead of selectedKeys
        const currentFilterValue = filteredColumnsInfo[column.dataIndex] || '';
        const selectedFilters = currentFilterValue ? [currentFilterValue] : [];

        return (
          <StatusFilter
            statusFilters={column.statusFilters}
            selectedFilters={selectedFilters}
            onFilterChange={(filter) => {
              const value = filter[0] ? filter[0] : '';
              onSearch(value, column.dataIndex);
              filterProps.setSelectedKeys(value ? [value] : []);
              filterProps.confirm();
            }}
          />
        );
      }

      if (column.showNumberFilter) {
        return (
          <div style={{ padding: 8 }}>
            <InputNumber
              min={1}
              value={filteredColumnsInfo[column.dataIndex]}
              placeholder={column.filterPlaceholder ?? t('table.filters.placeholders.number')}
              controls={false}
              onPressEnter={(e) => {
                const value = (e.target as HTMLInputElement).value;
                if (value && !isNaN(Number(value))) {
                  onSearchInputChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>, column.dataIndex);
                  filterProps.confirm({ closeDropdown: true });
                  onSearch(value, column.dataIndex);
                } else {
                  filterProps.confirm({ closeDropdown: true });
                  onSearch('', column.dataIndex);
                }
              }}
              onChange={(value) => {
                onSearchInputChange({ target: { value } } as React.ChangeEvent<HTMLInputElement>, column.dataIndex);
              }}
            />
          </div>
        );
      }

      return (
        <div style={{ padding: 8 }}>
          <Search
            allowClear
            enterButton
            value={filteredColumnsInfo[column.dataIndex]}
            onChange={(e) => onSearchInputChange(e, column.dataIndex)}
            onSearch={(value) => {
              filterProps.confirm({ closeDropdown: true });
              onSearch(value, column.dataIndex);
            }}
            placeholder="type to search ..."
          />
        </div>
      );
    };

  const onSearchInputChange = (event: React.ChangeEvent<HTMLInputElement>, dataIndex: string) => {
    setFilteredColumnsInfo({
      ...filteredColumnsInfo,
      [dataIndex]: event?.target?.value,
    });
  };

  const actionsDropdownRenderer = (record: any) => {
    if (actions.length === 0) return null;

    const convertActionsToMenuItems = (actionsList: ITableAction[]): MenuProps['items'] => {
      return actionsList.map((action) => ({
        key: action.key,
        label: action.label?.(record) ?? '',
        onClick: (e) => {
          e.domEvent.stopPropagation();
          action.onClick?.(record);
        },
        disabled: action.disabled?.(record) ?? false,
        ...('subMenu' in action &&
          (action as ITableActionWithSubmenu).subMenu && {
            children: convertActionsToMenuItems((action as ITableActionWithSubmenu).subMenu!),
          }),
      }));
    };

    const menuItems = convertActionsToMenuItems(actions);

    return (
      <Dropdown className="group-hover:flex" menu={{ items: menuItems }} trigger={['click']}>
        <MoreOutlined
          className="h2-bold text-blackShade-200"
          onClick={(e) => e.stopPropagation()}
          data-testid="moretesticon"
        />
      </Dropdown>
    );
  };

  const enhancedColumnsRendered = (record: any) => (
    <Space>
      {showView && <Button onClick={(e) => onView(e, record)} icon={<EyeOutlined />} data-testId="view-icon" />}
      {showEdit && <Button onClick={(e) => onEdit(e, record)} icon={<EditOutlined />} data-testId="delete-icon" />}
      {showRemove && (
        <Popconfirm
          title={delPopConfirmConf.title}
          description={delPopConfirmConf.desc}
          onConfirm={(e) => onRemove(e, record)}
          onCancel={(e) => e?.stopPropagation()}
          okText="Yes"
          cancelText="No"
        >
          <Button danger icon={<DeleteOutlined />} data-testId="edit-icon" onClick={(e) => e.stopPropagation()} />
        </Popconfirm>
      )}
      {showMenu && actionsDropdownRenderer(record)}
      {showAddTripToInvoiceBtn && (
        <Button
          type="primary"
          className="w-24"
          onClick={() => {
            console.log('record', record);
            onAddTripToInvoice?.(record.id, record.tripName);
          }}
          disabled={selectedTrips.some((trip) => trip.value === record.id)}
        >
          {selectedTrips.some((trip) => trip.value === record.id)
            ? t('financials.addTripModal.actions.added')
            : t('financials.addTripModal.actions.add')}
        </Button>
      )}
      {showViewTripDetailsBtn && (
        <Button
          type={'primary'}
          onClick={() => onViewTripDetails?.(record.id, record.customerId)}
          data-testId="view-icon"
        >
          {t('financials.details.invoicedTrips.actions.view')}
        </Button>
      )}
    </Space>
  );

  let enhancedColumns = columns.map((column: any) => {
    const baseColumn = {
      sorter: column.sorter,
      filterSearch: column.filterSearch,
      sortDirections: sortDirections,
      filteredValue: filteredColumnsInfo[column.dataIndex],
      filterIcon: column.filterIcon,
      filterDropdown: column.filterDropdown,
      ...column,
    };

    if (column.searchable === false) {
      return {
        ...baseColumn,
        filterIcon: undefined,
        filterDropdown: undefined,
      };
    }

    if (!column.filterIcon && !column.filterDropdown) {
      return {
        ...baseColumn,
        filterIcon: (filtered: any) =>
          column.showStatusFilter ? (
            <FilterFilled />
          ) : (
            <SearchOutlined style={{ color: filtered ? '#5D81FF' : undefined }} data-testid="searchIcon" />
          ),
        filterDropdown: getFilterDropdownRenderer(column),
      };
    }

    return baseColumn;
  });

  if (showView || showEdit || showRemove || showMenu || showAddTripToInvoiceBtn || showViewTripDetailsBtn) {
    enhancedColumns = [
      ...enhancedColumns,
      {
        title: '',
        key: 'actions',
        align: 'right',
        width: 30,
        render: enhancedColumnsRendered,
      },
    ];
  }

  const rowSelection = showSelection
    ? {
        onChange: onSelectionChange,
        selectedRowKeys: selectedRowKeys,
      }
    : undefined;

  // Check if onRow is provided in otherProps to determine if rows are clickable
  const hasRowClick = !!otherProps.onRow;

  // Determine scroll configuration based on screen size and data
  const getScrollConfig = () => {
    // On mobile/tablet screens (< 768px), always enable horizontal scroll
    if (isMobileOrTablet) {
      return { x: 'max-content' };
    }
    // On larger screens, only enable scroll when there's data
    if (dataSource && dataSource.length > 0) {
      return { x: 'max-content' };
    }
    // No scroll needed
    return {};
  };

  return (
    <Table
      scroll={getScrollConfig()}
      columns={enhancedColumns}
      dataSource={dataSource}
      rowSelection={rowSelection}
      className={hasRowClick ? 'clickable-rows' : ''}
      pagination={
        (pageInfo?.totalItems ?? 0) > (pageInfo?.size || 10)
          ? {
              pageSize: Number(pageInfo?.size) || 10,
              current: Number(pageInfo?.page) + 1,
              total: pageInfo?.totalItems,
              showTotal: (total, range) =>
                t('table.pagination.results', {
                  start: formatNumber(range[0]),
                  end: formatNumber(range[1]),
                  total: formatNumber(total),
                }),
              itemRender: (current, type, originalElement) => {
                if (type === 'page' && originalElement) {
                  return cloneElement(originalElement as ReactElement, {
                    children: formatNumber(current),
                  });
                }
                return originalElement;
              },
              responsive: true,
              showSizeChanger: false,
            }
          : false
      }
      locale={{
        emptyText: loading ? <Skeleton active paragraph={{ rows: dataSource?.length ?? 5 }} /> : <Empty />,
      }}
      {...otherProps}
    />
  );
};

EnhancedTable.defaultProps = {
  showEdit: true,
  showRemove: true,
  showView: true,
  showSearch: true,
  showSort: true,
  showSelection: false,
  onView: () => {},
  setFilteredColumnsInfo: () => {},
  onSelectionChange: () => {},
};

export default EnhancedTable;
